import type { SharedData } from "@src/context/ApplicationContext";
import { useApplicationContext } from "@src/context/ApplicationContext";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useRef } from "react";

/**
 * A resolver extracts a single form field value from the shared
 * ApplicationContext data (populated by CanvasPrefillController).
 * Return `undefined` to skip the field entirely.
 */
type FieldResolver = (data: Partial<SharedData>) => unknown;

export type CanvasPrefillFieldMap<T> = Partial<Record<keyof T, FieldResolver>>;

/**
 * Syncs Canvas-prefilled data from ApplicationContext into a form's local
 * state.  Runs whenever `canvasPrefillVersion` increments (set by
 * CanvasPrefillController after processing a Canvas event).
 *
 * Every resolved field is written unconditionally — the `appliedVersionRef`
 * already prevents the same event from being applied twice, so a version
 * bump always represents genuinely new data that should overwrite any
 * existing local values.
 *
 * @param setter - The form's state setter (e.g. `setValue` from `useState`).
 * @param fieldMap - Maps form field names to resolvers that extract values
 *                   from `SharedData`.
 */
export function useCanvasPrefillSync<T extends Record<string, unknown>>(
  setter: Dispatch<SetStateAction<T>>,
  fieldMap: CanvasPrefillFieldMap<T>,
): void {
  const { data } = useApplicationContext();
  const appliedVersionRef = useRef(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only re-run when canvasPrefillVersion changes. setter/fieldMap are stable caller references, data is read inside the effect.
  useEffect(() => {
    const version = data.canvasPrefillVersion ?? 0;
    if (!version || version <= appliedVersionRef.current) return;
    appliedVersionRef.current = version;

    setter((prev) => {
      const patch: Partial<T> = {};
      for (const [key, resolver] of Object.entries(fieldMap)) {
        if (!resolver) continue;
        const resolved = resolver(data);
        if (resolved === undefined) continue;
        (patch as Record<string, unknown>)[key] = resolved;
      }
      return Object.keys(patch).length > 0 ? { ...prev, ...patch } : prev;
    });
  }, [data.canvasPrefillVersion]);
}
